import React, { useState } from 'react'

import { color, MiscUtils, FormatUtils, RandomId, IntlNumberParser } from '@my/util'
import { Edit3, Info, RefreshCw, Trash2 } from '@tamagui/lucide-icons-2'
import { Controller, useForm } from 'react-hook-form'
import type {UseFormReturn} from 'react-hook-form';
import {
  ActionButton,
  Card,
  Text,
  XStack,
  YGroup,
  Separator,
  ListItem,
  Group,
  YStack,
  SimpleText,
  Image, View, Paragraph,
  TextInputComponent,
  ScrollView,
  FinancialTable,
  CustomCheckboxInput,
  Button, Label, Select, useMedia
} from '@my/ui'
import images from '../../../assets/images'


import { DropdownField } from '../helpers/Helper'
import { AccountTypes, confirmationOptions, PlaidAccountTypes } from '../data'
import { shadowGenerator } from '../../../utils/commonStyles'
import { defaultTo, groupBy, pick } from 'lodash'

const NameRegex = /^[a-zA-Z0-9_@./#&+\-\s]{2,}$/;
const FinancialAmountRegex = /^\$?\d+(,?\d{3})*(\.\d{1,2})?$/;

const TextInput: React.FC<{
  value: string
  onChangeText: (text: string) => void
  onBlur: () => void
  placeHolder: string
  error?: any;
  isNumeric?: boolean;
}> = ({ value, onChangeText, onBlur, placeHolder, error, isNumeric }) => (
  <XStack gap={15} width={'100%'} minWidth={300} style={{justifyContent: 'center'}}>
    <XStack style={{flexGrow: 1}}>
      <TextInputComponent
        isPassword={false}
        height={14}
        width={7}
        size={17}
        inputWidth={'100%'}
        onChangeText={onChangeText}
        onBlur={onBlur}
        value={value}
        keyboardType={isNumeric ? "numeric": 'default'}
        justifyContent={'flex-start'}
        placeHolderText={placeHolder ?? ''}
        tintColor={color.opaqueGrey}
        customInputStyle={{
          width: '100%',
          ...(error ? { borderColor: 'red' } : {})
          //borderBottom: '2px solid rgb(133, 193, 157)'
        }}
        error={error}
      />
    </XStack>
  </XStack>
)

// ExpenseInput Component
const ExpenseInput: React.FC<{
  value: string
  onChangeText: (text: string) => void
  onBlur: () => void
  placeHolder: string
  error?: any;
}> = (props) => <TextInput {...props} isNumeric={true} />

const upperCaseWord = (word: string) => word.slice(0,1).toUpperCase() + word.slice(1)

const AddAccountCard = ({ formObjects, add, cancel, isEdit }: {formObjects: UseFormReturn, add: any, cancel: any, isEdit: boolean}) => {
  const {control, watch, formState: { isValid } } = formObjects;

  const type = watch('type') as keyof typeof PlaidAccountTypes;
  const itemType = watch('itemType');
  const subtype = watch('subtype');
  const isInterestBearing = watch("Is High-Yield or Interest Earning");
  const isFinancialAccount = itemType === 'financial-account';
  let accountNameLabel = 'Item Name';
  let amountLabel = 'Item Value';
  let rateLabel = 'Rate';
  if (isFinancialAccount)  {
    accountNameLabel = 'Account Name';
    amountLabel = 'Amount';
    if (type !== 'investment') {
      amountLabel = 'Account Balance';
      rateLabel = `Interest Rate${['loan', 'credit'].includes(type) ? ' (APR)' : ''}`;
    } else {
      rateLabel = 'Rate of Return (average)'
    }
  }
  const onSubmit = () => {
    if (isFinancialAccount) {
      if (!(type === 'depository' && subtype === 'checking')) {
        formObjects.unregister("Is Primary Checking Account");
      }
      if (type === 'depository' && subtype === 'checking') {
        formObjects.unregister("Is High-Yield or Interest Earning");
      }
    } else {
      const unregisterList = Object.keys(formObjects.formState.dirtyFields).filter((fieldName) => !['name', 'amount'].includes(fieldName));
      formObjects.unregister(unregisterList);
    }
    add();
  }
  return (
    <YStack gap={20}>
      {/* Separator with OR */}
      <Card padding={10} boxShadow={'0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)'}>
        <Card.Header zIndex={1}>
          <SimpleText fontWeight="600" fontSize={18} textAlign="center" color={'rgb(73, 80, 87)'}>
            Add Account Manually
          </SimpleText>
        </Card.Header>

        <DropdownField
          control={control}
          name="itemType"
          placeholder="What type of item are you adding?"
          items={[
            {value: 'financial-account', label: 'Financial Account'},
            {value: 'real-estate', label: 'Real Estate'},
            {value: 'personal-property', label: 'Personal Property'},
            {value: 'financial-interest', label: 'Business, Private Equity, or Misc. Financial Interest'},
          ]}
          native
        />
        {/* <Label htmlFor="itemType">What type of item are you adding?</Label>
        <CustomCheckboxInput
          options={[
            {value: 'financial-account', label: 'Financial Account'},
            {value: 'real-estate', label: 'Real Estate'},
            {value: 'personal-property', label: 'Personal Property'},
            {value: 'financial-interest', label: 'Business, Private Equity, or Misc. Financial Interest'},
          ]}
          control={control}
          name="itemType"
          circle
        /> */}
        {isFinancialAccount && <YStack space="$1">
          <Label htmlFor="institutionName">Financial Institution Name</Label>
          <Controller
            control={control}
            name={'institutionName'}
            rules={{required: true, pattern: NameRegex}}
            render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
              <TextInput
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                placeHolder={'Enter Financial Institution Name'}
                error={error}
                
              />
            )}
          />
        </YStack>}

        <YStack space="$1">
          <Label htmlFor="name">{accountNameLabel}</Label>
          <Controller
            control={control}
            name={'name'}
            rules={{required: true, pattern: NameRegex}}
            render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
              <TextInput
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                placeHolder={'Enter ' + accountNameLabel}
                error={error}
              />
            )}
          />
        </YStack>

        {isFinancialAccount ? <>
          <YStack space="$2">
            <DropdownField
              control={control}
              name="type"
              placeholder="Account Type"
              items={Object.entries(PlaidAccountTypes).map(([key, value]) => ({value: key, label: upperCaseWord(key)+` (${value.description})`}))}
              native
              showLabel
              rules={{required: true}}
            />
          </YStack>

          {type && PlaidAccountTypes[type].subTypes.length ? <YStack space="$2">
            <DropdownField
              control={control}
              name="subtype"
              placeholder="Account Sub-Type"
              items={(PlaidAccountTypes[type] || {}).subTypes?.map((val) => ({value: val.name, label: val.description})) || []}
              native
              showLabel
              rules={{required: true}}
            />
          </YStack> : null}

          {(type === 'depository' && subtype === 'checking') ? <YStack space="$2">
            <Label htmlFor="Is Primary Checking Account">{'Is this your primary checking account?'}</Label>
            <CustomCheckboxInput
              options={confirmationOptions} // Checkbox options for dependents
              control={control}
              name="Is Primary Checking Account"
              rules={{required: true}}
            />
          </YStack> : null}
          <YStack space="$1">
            <Label htmlFor="amount">{amountLabel}</Label>
            <Controller
              control={control}
              name={'amount'}
              rules={{required: true, pattern: FormatUtils.regex.financialAmount}}
              render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
                <ExpenseInput
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  placeHolder={'Enter ' + amountLabel}
                  error={error}
                />
              )}
            />
          </YStack>
          {(type === 'depository' && subtype !== 'checking') ? <YStack space="$2">
            <Label htmlFor="Is High-Yield or Interest Earning">{'Is this account high-yield or interest earning?'}</Label>
            <CustomCheckboxInput
              options={confirmationOptions} // Checkbox options for dependents
              control={control}
              name="Is High-Yield or Interest Earning"
              rules={{required: true}}
            />
          </YStack> : null}
          {(type === 'credit' || type === 'loan') ? <YStack space="$2">
            <Label htmlFor="Credit Limit/Max Loan Amount">{'What is the credit limit or max (original) loan amount?'}</Label>
            <Controller
              control={control}
              name="Credit Limit/Max Loan Amount"
              render={({ field: { onChange, value, onBlur } }) => (
                <ExpenseInput
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  placeHolder={'Enter amount'}
                />
              )}
            />
          </YStack> : null}

          {(['credit', 'loan', 'investment'].includes(type) || (type === 'depository' && isInterestBearing)) && <YStack space="$2">
            <Label htmlFor="rate">What is the {rateLabel}?</Label>
            <Controller
              control={control}
              name={'rate'}
              render={({ field: { onChange, value, onBlur } }) => (
                <ExpenseInput
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  placeHolder={'Enter '+rateLabel}
                />
              )}
            />
          </YStack>}
          {(subtype !== 'checking') ? <YStack space="$2">
            <Label htmlFor="Monthly Contribution to this Account">{'How much money do you pay to this account per month?'}</Label>
            <Controller
              control={control}
              name="Monthly Contribution to this Account"
              render={({ field: { onChange, value, onBlur } }) => (
                <ExpenseInput
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  placeHolder={'Enter Monthly amount'}
                />
              )}
            />
          </YStack> : null}
        </> : <>
          <YStack space="$1">
            <Label htmlFor="amount">{amountLabel}</Label>
            <Controller
              control={control}
              name={'amount'}
              rules={{required: true}}
              render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
                <ExpenseInput
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  placeHolder={'Enter ' + amountLabel}
                  error={error}
                />
              )}
            />
          </YStack>
        </>}

        <Card.Footer zIndex={1}>
          <XStack marginLeft={'auto'} marginTop={25} alignItems='center' gap={15} style={{flexWrap: 'wrap', width: '100%'}}>
            {isValid ? null : <SimpleText color='red' marginRight="auto">Fix errors before saving changes.</SimpleText>}
            <XStack gap={15}>
              <Button backgroundColor={'pink'} onPress={() => cancel()}>Cancel</Button>
              <Button {...(isValid ? {} : {disabled: true, opacity: 0.5})} backgroundColor={color.tzaGreen} onPress={() => onSubmit()}>{isEdit ? 'Save' : 'Add'}</Button>
            </XStack>
          </XStack>
        </Card.Footer>
      </Card>
    </YStack>
  )
}

interface LinkedFinancialAccountsProps {
  linkedFinancialData: Record<string, any>
  refreshFinancialInstitution: (institution_obj: any) => Promise<any>
  deleteFinancialInstitution: (institution_obj: any) => void
  ready: boolean
  open: Function
  control: any
}

export const PlaidInstitutionToRows = (institution_obj: any, valueTransformers?: Record<string, (x: any) => any>) => ((institution_obj?.accounts as any[] || []).map((acct) => {
  const itemType = defaultTo(acct.itemType, 'financial-account');
  const institutionName = itemType === 'financial-account'
  ? institution_obj.institution?.name || ''
  : 'Personal Holdings'

  const row = {
    institution_id: institution_obj.institution?.institution_id || RandomId(),
    institutionName,
    name: acct.name || '',
    type: acct.type || '',
    subtype: acct.subtype || '',
    amount: acct.balances?.available || acct.balances?.current || '',
    rate: acct.rate || '',
    itemType,
    ...(acct.details || {})
  };
  if (valueTransformers) {
    Object.entries(valueTransformers || {}).forEach(([key, fn]) => {
      if (row[key]) {
        row[key] = fn(row[key]);
      } 
    });
  }
  return row;
}))

export const FinancialAccountsTableRows = (linkedFinancialData: Record<string, any>) => groupBy(Object.values(linkedFinancialData || {}).map((iObj) => PlaidInstitutionToRows(iObj, {amount: (v) => FormatUtils.currency.cashFormat(v)})).flat(), 'type')

const FinancialAccountsTable_HEADINGS = [
  //{ key: 'type', text: 'Account Type' },
  { key: 'subtype', text: 'Account Sub-Type' },
  { key: 'institutionName', text: 'Financial Institution' },
  { key: 'name', text: 'Account Name' },
  { key: 'amount', text: 'Amount' },
]

export const FinancialAccountsTable: React.FC<{
  linkedFinancialData: Record<string, any>;
  type?: keyof typeof PlaidAccountTypes | Array<keyof typeof PlaidAccountTypes>
}> = ({linkedFinancialData, type}) => {
  const types = type 
  ? Array.isArray(type) ? type : [type]
  : undefined;
  const tableRows = types
  ? types.map(innerType => FinancialAccountsTableRows(linkedFinancialData)[innerType] || []).flat()
  : Object.values(FinancialAccountsTableRows(linkedFinancialData)).flat();
  return <FinancialTable 
    data={tableRows}
    headings={FinancialAccountsTable_HEADINGS}
    noDataMessage={`No ${types?.length === 1 ? type+' ': ''}accounts added.`}
    mobileRenderOptions={{
      titleKey: 'name',
      mainValueKeys: ['amount'],
      otherKeys: [['subtype','institutionName']]
    }}
  />
};

export const LinkedFinancialAccounts: React.FC<LinkedFinancialAccountsProps> = ({
  linkedFinancialData,
  refreshFinancialInstitution,
  deleteFinancialInstitution,
  ready,
  open,
  control,
}) => {
  const media = useMedia();
  const [plaidInfo, setPlaidInfo] = useState(false);
  const togglePlaidInfo = () => setPlaidInfo((prev) => !prev)
  const defaultValues = {
    itemType: 'financial-account',
    type: 'depository',
    subtype: 'checking'
  };
  const formObjects = useForm<Partial<{institution_id: string; institutionName: string; name: string; type: string; subtype: string; amount: string; rate: string; itemType: string;}>>();
  const [manualAccountObj, setManualAccountOpen] = useState<{open: boolean; edit?: boolean}>({open: false, edit: false})
  const openManualAccountModal = (institution_obj?: any) => {
    //if (institution_obj) {
    const iObj = institution_obj ? institution_obj : { accounts: [defaultValues] };
      const formValues = PlaidInstitutionToRows(iObj)[0];
      Object.entries(formValues).forEach(([name, value]) => formObjects.setValue(name as any, value, { shouldDirty: true, shouldTouch: true}));
    //}
    setManualAccountOpen({open: true, edit: !!institution_obj});
  }
  const manualAccountAdd = async () => {
    formObjects.handleSubmit(async (formValues) => {
      //const formValues = formObjects.getValues();
      const itemType = defaultTo(formValues.itemType, 'financial-account');
      const institutionName = formValues.itemType === 'financial-account'
      ? formValues.institutionName || ''
      : 'Personal Holdings'/* itemType === 'real-estate'
      ? 'Real Estate'
      : itemType === 'personal-property'
      ? 'Personal Property'
      : 'Financial Interest' */;
      const institution_obj = {
        institution: {
          name: institutionName,
          institution_id: formValues.institution_id
        },
        accounts: [
          {
            name: formValues.name,
            type: formValues.itemType === 'financial-account' ? formValues.type : 'misc',
            subtype: formValues.itemType === 'financial-account' ? formValues.subtype : formValues.itemType,
            balances: { available: IntlNumberParser(String(formValues.amount || "").replace('$', '')) },
            rate: formValues.rate,
            itemType,
            details: pick(formValues, [
              "Is Primary Checking Account",
              "Is High-Yield or Interest Earning",
              "Credit Limit/Max Loan Amount",
              "Monthly Contribution to this Account"
            ])
          }
        ],
        isManual: true
      };
      await refreshFinancialInstitution(institution_obj);
      //control.setValue('linkedFinancialData', Object.assign({}, linkedFinancialData, { [formValues.institution_id as any]: institution_obj }, { shouldDirty: true, shouldTouch: true}))
      formObjects.reset();
      setManualAccountOpen({open: false});
    })();
    
  }
  const manualAccountCancel = () => {
    formObjects.reset();
    setManualAccountOpen({open: false});
  }
  return (
    <YStack gap={20} width={'100%'} paddingHorizontal={5}>
      <SimpleText fontSize={18} fontWeight={'600'} color={color.grey}>
        Add your Financial Accounts
      </SimpleText>
      {manualAccountObj.open ? null : <>
        <YStack style={{alignItems: 'center'}}>
          <ActionButton
            unstyled
            onPress={() => open()}
            disabled={!ready}
            borderWidth={1}
            borderColor={color.tzaGreen}
            hoverStyle={{backgroundColor: '#e7e7e7'}}
            borderStyle="dashed"
            height={90}
            bg={color.white}
            style={{ alignItems: 'center', justifyComment: 'center', paddingTop:10, width: '100%' }}
            borderRadius={10}
            gap={5}
            icon={
              <Image
                src={images.linkAccount}
                style={{ height: 35, width: 45 }}
                resizeMode="contain"
              />
            }
          >
            <SimpleText fontSize={14} fontWeight={'700'} color={color.grey}>
              Link Account Automatically
            </SimpleText>
          </ActionButton>
          <XStack style={{alignItems: 'center', gap: 5}}>
            <Text style={{fontStyle: 'italic', fontSize: 12, textAlign: 'center', lineHeight: 12, width: '90%'}}>Powered by Plaid · Bank-level encryption · We never see your login</Text>
            <View style={{height: 16, width: 16, borderRadius: 100, cursor: 'pointer'}} 
              hoverStyle={{backgroundColor:'rgb(240,240,240)'}}
              pressStyle={{backgroundColor:'rgb(240,240,240)'}}
              onPress={togglePlaidInfo}
            ><Info style={{height: 16, width: 16}} /></View>
          </XStack>
          {plaidInfo ? <YStack style={{marginTop: 10}}>
            <Paragraph style={{
              fontSize: 12, borderStyle: 'solid', borderWidth: 1, 
              borderColor: 'rgba(133, 193, 157, 0.17)', borderRadius: 10, padding: 10, 
              backgroundColor:'#F9F9F9'
            }}>
              When you click "Link Account Automatically," you'll be taken to Plaid's secure login screen — not Parity's. You enter your credentials directly with Plaid, and we never see, store, or have access to them. Plaid is the same service used by Venmo, Robinhood, Chime, and thousands of other apps to securely connect to over 12,000 banks. Parity only receives read-only access to balances and transactions, so we can't move money or do anything beyond what's needed to give you insights. All data is encrypted in transit and at rest, and you can disconnect or request full deletion at any time.
            </Paragraph>
          </YStack> : null}
        </YStack>
        <Text fontWeight={700} color={color.lightGrey} margin={'auto'}>-- OR --</Text>
        <YStack style={{alignItems: 'center'}}>
          <ActionButton
            unstyled
            onPress={() => openManualAccountModal()}
            disabled={!ready}
            borderWidth={1}
            borderColor={color.tzaGreen}
            hoverStyle={{backgroundColor: '#e7e7e7'}}
            borderStyle="dashed"
            height={90}
            style={{ alignItems: 'center', justifyComment: 'center', paddingTop:10, width: '100%' }}
            borderRadius={10}
            gap={5}
            icon={
              <Image
                src={images.linkAccount}
                style={{ height: 35, width: 45 }}
                resizeMode="contain"
              />
            }
          >
            <SimpleText fontSize={14} fontWeight={'700'} color={color.grey}>
              Add Account, Holding, or Interest Manually
            </SimpleText>
          </ActionButton>
          <Text style={{fontStyle: 'italic', fontSize: 12, textAlign: 'center', lineHeight: 12}}>No bank login required · Enter only what you're comfortable sharing</Text>
        </YStack>
        <XStack
          flexWrap="wrap"
          justifyContent="center"
          width='100%'
          style={{width: '100%'}}
          //$sm={{ flexDirection: 'column', alignItems: 'center', gap: 10 }}
          padding="$2"
          gap={10}
        >
          {Object.entries(linkedFinancialData || {}).map(
            ([institution_id, institution_obj]: [string, any]) => (
              <Card
                key={institution_id}
                elevate
                bg={color.white}
                maxWidth={340}
                width="100%"
                {...shadowGenerator(0.3)}
              >
                <Card.Header gap={10} width="100%" style={{zIndex: 1}}>
                  <XStack alignItems={'center'} $sm={{flexDirection: 'column', alignItems: 'flex-start', gap: 5}}>
                    <XStack gap={10} alignItems={'center'} width={220} $sm={{width: '100%'}}>
                      <Image
                        src={images.bank}
                        style={{ height: 30, width: 30 }}
                        resizeMode="contain"
                      />
                      <SimpleText fontSize={16} color={color.grey} fontWeight={'600'}>
                        {institution_obj.institution.name}
                      </SimpleText>
                    </XStack>
                    <XStack gap={10}>
                      {institution_obj.isManual ? <ActionButton
                        size="$2"
                        icon={<Edit3 />}
                        onPress={() => openManualAccountModal(institution_obj)}
                      /> : <ActionButton
                        size="$2"
                        icon={<RefreshCw />}
                        onPress={() => refreshFinancialInstitution(institution_obj)}
                      />}
                      <ActionButton
                        size="$2"
                        color="red"
                        icon={<Trash2 />}
                        onPress={() => deleteFinancialInstitution(institution_obj)}
                      />
                    </XStack>
                  </XStack>
                  <XStack gap={8} alignItems={'center'}>
                    <Image
                      src={images.clock}
                      style={{ height: 14, width: 14 }}
                      resizeMode="contain"
                    />
                    <Text fontSize={12} color={color.lightGrey}>
                      Last {institution_obj.isManual ? 'edited' : 'synced'}: {new Date(institution_obj.timestamp).toLocaleString()}
                    </Text>
                  </XStack>
                </Card.Header>
                <YGroup alignSelf="center" bordered width={240} size="$5" separator={<Separator />} marginBottom={20}>
                  {[...(institution_obj.accounts || [])].map((account_obj) => {
                    const balance =
                      account_obj.value || account_obj.balances?.available || account_obj.balances?.current || 0
                    const fmt = FormatUtils.currency.cashFormat(balance)
                    return (
                      <YGroup.Item key={account_obj.account_id}>
                        <ListItem
                          hoverTheme
                          pressTheme
                          title={account_obj.name + (account_obj.mask ? ' - ' + account_obj.mask : '')}
                          subTitle={account_obj.type + ' - ' + account_obj.subtype}
                        >
                          <ListItem.Text>{fmt}</ListItem.Text>
                        </ListItem>
                      </YGroup.Item>
                    )
                  })}
                </YGroup>
              </Card>
            )
          )}
        </XStack>
      </>}
      {manualAccountObj.open ? <AddAccountCard formObjects={formObjects} add={() => manualAccountAdd()} cancel={() => manualAccountCancel()} isEdit={!!manualAccountObj.edit} /> : null}
      {media['max-md'] ? null : <FinancialAccountsTable linkedFinancialData={linkedFinancialData} /* type={['depository', 'credit', 'investment', 'loan', 'other']} */ /> }
    </YStack>
  )
}
